import { debounce } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  useGetConfigurationQuery,
  useGetRecipientsQuery,
  useWithdrawIntegratorEarningsMutation,
} from '@/api/v1';
import { useGetIntegratorTotalEarningsQuery } from '@/api/v1';
import { FlexibleSelectTextField } from '@/components/atoms/FlexibleSelectTextField';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { Section } from '@/components/molecules/Section';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { Header } from '@/components/organisms/Header';
import { Currency } from '@/data/currencies/type';
import { useBalances } from '@/hooks/useBalances';
import { useCustomer } from '@/hooks/useCustomer';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useToast } from '@/hooks/useToast';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import {
  amountChanged,
  DestinationType,
  destinationTypeChanged,
  EarningsClaimAction,
  EarningsClaimFlowState,
  earningsClaimReducer,
  initialState,
  recipientChanged,
} from '@/Screens/Dashboard/IntegratorEarnings/EarningsClaimFlow/reducer';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { Page } from '@/types/page';
import { RecipientResponse } from '@/types/recipient';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import {
  getRecipientAccountNumber,
  getRecipientCurrency,
  getRecipientDisplayName,
  isRecipientAvailableForTransaction,
} from '@/utility/recipientDisplayAdapter';
import { AsAnyActionArg } from '@/utility/reducers';

enum FormErrors {
  MISSING_AMOUNT = 'MISSING_AMOUNT',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
  MISSING_RECIPIENT = 'MISSING_RECIPIENT',
}

export const EarningsClaimFlow: React.FC = (): React.ReactElement => {
  const customer = useCustomer();
  const toast = useToast();
  const tr = useTranslator();
  const navigate = useNavigate();
  const [error, setError] = React.useState<FormErrors | null>(null);
  const locale = React.useContext(LocaleContext);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const walletBalances = useBalances({
    currencies: [CurrencyCodeType.USDC],
  });

  const usdcCurrency = Currency.fromCode(locale, CurrencyCodeType.USDC, displayUsdcAsUsd);
  const UsdcIcon = usdcCurrency.icon;
  const usdcBalance =
    walletBalances[CurrencyCodeType.USDC] ?? DetailedBalance.zero(CurrencyCodeType.USDC);

  const { data: earningsData, isLoading: loadingEarnings } = useGetIntegratorTotalEarningsQuery();

  const {
    data: configuration,
    isLoading: isLoadingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const availableBalance = React.useMemo(
    (): number => earningsData?.unpaidBalance ?? 0,
    [earningsData],
  );

  const { data: recipientsPage = Page.empty<RecipientResponse>(), isFetching: fetchingAccounts } =
    useGetRecipientsQuery(
      { beneficiaryId: customer.beneficiaryId, belongsToBeneficiary: true, size: 200 },
      { skip: !customer.beneficiaryId },
    );

  const ownAccounts = React.useMemo(
    (): readonly RecipientResponse[] =>
      recipientsPage.content.filter((recipient: RecipientResponse): boolean =>
        isRecipientAvailableForTransaction(recipient),
      ),
    [recipientsPage],
  );

  const reducer = React.useReducer<EarningsClaimFlowState, AsAnyActionArg<EarningsClaimAction>>(
    earningsClaimReducer,
    initialState,
  );

  const [withdrawEarnings, mutation] = useWithdrawIntegratorEarningsMutation();
  const [state, dispatch] = reducer;
  const {
    destinationType,
    fromAmount,
    targetCurrency,
    recipientId,
    paymentRail,
    onlyMoveToCustody,
  } = state;

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }

    return (
      isLoadingConfiguration ||
      isBRLOperationOutsideMarketHours(targetCurrency, configuration.brazilianMarketHours)
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isLoadingConfiguration,
    targetCurrency,
  ]);

  const onClose = React.useCallback((): void => {
    navigate('/earnings');
  }, [navigate]);

  const onDestinationTypeChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: string): void => {
      dispatch(destinationTypeChanged(value as DestinationType));
      setError(null);
    },
    [dispatch],
  );

  const onRecipientChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (error === FormErrors.MISSING_RECIPIENT) {
        setError(null);
      }
      const recipient = ownAccounts.find(
        (r: RecipientResponse): boolean => r.id === event.target.value,
      );
      if (recipient) {
        dispatch(
          recipientChanged({
            recipientId: recipient.id,
            paymentRail: recipient.type,
            currency: recipient.currency,
          }),
        );
      }
    },
    [dispatch, ownAccounts, error],
  );

  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      if (error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE || error === FormErrors.MISSING_AMOUNT) {
        setError(null);
      }
      dispatch(amountChanged(value));
    },
    [error, dispatch],
  );

  const formatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const currencyFormatter = useLocalizedFormatter({
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedBalance = React.useMemo(
    (): string => formatter.format(availableBalance),
    [formatter, availableBalance],
  );

  const onSetMaxAmount = React.useCallback((): void => {
    onFromAmountChange(availableBalance);
  }, [onFromAmountChange, availableBalance]);

  const loading = React.useMemo(
    (): boolean => loadingEarnings || fetchingAccounts || mutation.status === QueryStatus.pending,
    [loadingEarnings, fetchingAccounts, mutation.status],
  );

  const onConfirmWithdrawal = React.useCallback((): void => {
    if (!fromAmount) {
      return setError(FormErrors.MISSING_AMOUNT);
    }
    if (fromAmount > availableBalance) {
      return setError(FormErrors.AMOUNT_BIGGER_THAN_BALANCE);
    }
    if (destinationType === 'external' && !recipientId) {
      return setError(FormErrors.MISSING_RECIPIENT);
    }

    withdrawEarnings({
      disbursalAmount: {
        currencyCode: CurrencyCodeType.USDC,
        value: fromAmount,
      },
      targetCurrency,
      paymentRail,
      recipientId,
      onlyMoveToCustody,
    });
  }, [
    fromAmount,
    availableBalance,
    destinationType,
    recipientId,
    targetCurrency,
    paymentRail,
    onlyMoveToCustody,
    withdrawEarnings,
  ]);

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.fulfilled) {
      navigate('/earnings/claim/completed');
    }
    if (mutation.status === QueryStatus.rejected) {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [mutation.status, toast, navigate, tr]);

  const amountHelperText = React.useMemo(
    (): string | null =>
      match(error)
        .with(FormErrors.MISSING_AMOUNT, (): string => tr('REQUIRED_AMOUNT'))
        .with(FormErrors.AMOUNT_BIGGER_THAN_BALANCE, (): string =>
          tr('AMOUNT_EXCEEDS_AVAILABLE_EARNINGS', currencyFormatter.format(availableBalance)),
        )
        .otherwise((): null => null),
    [availableBalance, currencyFormatter, error, tr],
  );

  const destinationRenderer = React.useCallback((recipient: RecipientResponse): React.ReactNode => {
    const accountNumber = getRecipientAccountNumber(recipient);
    const currency = getRecipientCurrency(recipient);

    return (
      <MenuItem key={recipient.id} value={recipient.id}>
        <Box>
          <Typography variant="body2">{getRecipientDisplayName(recipient)}</Typography>
          {accountNumber && (
            <Typography variant="body1" color="text.secondary">
              {accountNumber}
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary">
            {currency} - {recipient.type}
          </Typography>
        </Box>
      </MenuItem>
    );
  }, []);

  const accountLabel = React.useMemo((): React.ReactNode => {
    if (fetchingAccounts) {
      return <Box sx={styles.loadingText} />;
    } else if (ownAccounts.length === 0) {
      return tr('NO_ACCOUNTS');
    } else {
      return tr('SELECT_ACCOUNT');
    }
  }, [ownAccounts.length, fetchingAccounts, tr]);

  const canConfirm = React.useMemo((): boolean => {
    if (!fromAmount) {
      return false;
    }
    if (destinationType === 'external' && !recipientId) {
      return false;
    }
    return true;
  }, [fromAmount, destinationType, recipientId]);

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title={tr('CLAIM_EARNINGS')} onClose={onClose} />
      <FullScreenModalContainer>
        <Section
          title={tr('DESTINATION_TYPE')}
          visible={true}
          working={mutation.status === QueryStatus.pending}
          spotlight={true}>
          <FormControl>
            <RadioGroup value={destinationType} onChange={onDestinationTypeChange}>
              <FormControlLabel
                value="caliza_wallet"
                control={<Radio />}
                label={tr('CALIZA_WALLET')}
                sx={styles.radioLabel}
              />
              <FormControlLabel
                value="external"
                control={<Radio />}
                label={tr('WITHDRAWAL_ACCOUNT')}
                sx={styles.radioLabel}
              />
            </RadioGroup>
          </FormControl>
        </Section>

        {destinationType === 'caliza_wallet' && (
          <Section
            title={tr('SELECT_TARGET_CURRENCY')}
            visible={true}
            working={loading}
            spotlight={true}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <UsdcIcon width={24} height={24} />
              <Box>
                <Typography fontSize={16} lineHeight="24px" fontWeight={400}>
                  {usdcCurrency.name}
                </Typography>
                <Typography
                  fontSize={16}
                  lineHeight="24px"
                  fontWeight={400}
                  color="custom.LightGray">
                  {tr('BALANCE')}: {currencyFormatter.format(usdcBalance.available)}
                </Typography>
              </Box>
            </Box>
          </Section>
        )}

        {destinationType === 'external' && (
          <Section
            title={tr('WITHDRAWAL_ACCOUNT')}
            visible={true}
            working={loading}
            spotlight={true}>
            <FlexibleSelectTextField
              label={accountLabel}
              select={true}
              fullWidth={true}
              disabled={loading}
              value={recipientId ?? ''}
              error={error === FormErrors.MISSING_RECIPIENT}
              helperText={error === FormErrors.MISSING_RECIPIENT ? tr('REQUIRED_ACCOUNT') : null}
              onChange={onRecipientChange}>
              {ownAccounts.map(destinationRenderer)}
            </FlexibleSelectTextField>
            {shouldNotAllowOperation && (
              <Box sx={styles.brlAlertContainer}>
                <OutsideBRLMarketHoursAlert />
              </Box>
            )}
          </Section>
        )}

        <Section title={tr('AMOUNT')} visible={true} working={loading} spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <AmountAndCurrencyWidget
              label={tr('YOU_CLAIM')}
              currencies={[CurrencyCodeType.USDC]}
              amount={fromAmount}
              currency={CurrencyCodeType.USDC}
              disabled={false}
              helperText={amountHelperText}
              error={!!amountHelperText}
              onAmountChange={debounce(onFromAmountChange, 300)}
              onCurrencyChange={(): void => {}}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" color="custom.LightGray">
                {tr('EARNINGS_AVAILABLE', formattedBalance, CurrencyCodeType.USDC)}
              </Typography>
              <Button variant="dark" size="small" sx={styles.maxButton} onClick={onSetMaxAmount}>
                {tr('MAX')}
              </Button>
            </Box>
          </Box>
        </Section>

        <Alert severity="info" sx={styles.infoAlert}>
          <Typography variant="body1" color="custom.LightGray">
            {tr('EARNINGS_CLAIM_NO_FEES')}
          </Typography>
        </Alert>

        <Box width={552} mx="auto">
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth={true}
            loading={loading}
            disabled={!canConfirm || shouldNotAllowOperation}
            onClick={onConfirmWithdrawal}>
            {tr('CONFIRM_EARNINGS_CLAIM')}
          </LoadingButton>
        </Box>
      </FullScreenModalContainer>
    </ApplicationDialog>
  );
};

const styles: Record<string, SystemStyleObject> = {
  radioLabel: {
    alignItems: 'center',
    color: 'text.primary',
  },
  maxButton: {
    padding: 0,
    width: 53,
    minWidth: 0,
  },
  brlAlertContainer: {
    mt: 2,
  },
  infoAlert: {
    width: 552,
    mx: 'auto',
  },
  loadingText: {
    my: 0.75,
    height: 16,
    width: 240,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
    borderRadius: 2,
  },
};
